import { graphql } from 'react-relay';
import { FormattedMessage, defineMessages } from 'react-intl';
import React, { useState } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { fg } from '@confluence/feature-gating';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';

import type {
	useRequestSpaceAccessMutationRequestSpaceAccessMutation as useRequestSpaceAccessMutationRequestSpaceAccessMutation$data,
	useRequestSpaceAccessMutationRequestSpaceAccessMutationVariables as useRequestSpaceAccessMutationRequestSpaceAccessMutation$variables,
} from './__types__/useRequestSpaceAccessMutationRequestSpaceAccessMutation.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { MutationFunctionInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
const useRequestSpaceAccessMutationRequestSpaceAccessMutation = graphql`
	mutation useRequestSpaceAccessMutationRequestSpaceAccessMutation($spaceKey: String!) {
		confluence {
			requestSpaceAccess(confluenceRequestSpaceAccessInput: { spaceKey: $spaceKey }) {
				spaceKey
				success
				errors {
					message
					extensions {
						statusCode
					}
				}
			}
		}
	}
`;
export const i18nFlags: {
	successFlagTitle: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	successFlagDescription: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	generalErrorFlagTitle: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	generalErrorFlagDescription: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	spaceCreatorUnavailableErrorFlagTitle: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	spaceCreatorUnavailableErrorFlagDescription: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	successFlagTitle: {
		id: 'no-permission.restricted.space.licensed-user.request-access.success-flag.title',
		defaultMessage: 'Request sent',
		description: 'flag message title for licensed users to show that their request was successful',
	},
	successFlagDescription: {
		id: 'no-permission.restricted.space.licensed-user.request-access.success-flag.description',
		defaultMessage: 'We’ve asked the admin of the space to give you access.',
		description:
			'flag message description for licensed users to show that their request was successful',
	},
	generalErrorFlagTitle: {
		id: 'no-permission.restricted.space.licensed-user.request-access.general-error-flag-title',
		defaultMessage: 'Request not sent',
		description:
			'Flag notification title informing licensed user that request encountered an error',
	},
	generalErrorFlagDescription: {
		id: 'no-permission.restricted.space.licensed-user.request-access.general-error-flag-description',
		defaultMessage: 'Refresh the page to resubmit your request, or try again later.',
		description:
			'Flag notification description informing licensed user that request encountered an error',
	},
	spaceCreatorUnavailableErrorFlagTitle: {
		id: 'no-permission.restricted.space.licensed-user.request-access.space-creator-unavailable-error-flag-title',
		defaultMessage: 'Can’t request access',
		description:
			'Flag notification title informing licensed user that request encountered an error due to the space creator not being available',
	},
	spaceCreatorUnavailableErrorFlagDescription: {
		id: 'no-permission.restricted.space.licensed-user.request-access.space-creator-unavailable-error-flag-description',
		defaultMessage:
			'This space’s access request functionality isn’t currently available. If you need access, reach out to an admin in your organization.',
		description:
			'Flag notification description informing licensed user that request encountered an error due to the space creator not being available',
	},
});

export const useRequestSpaceAccessMutation = ({
	flags,
}: WithFlagsProps): {
	updateSpaceAccessMutation: MutationFunctionInterop<
		useRequestSpaceAccessMutationRequestSpaceAccessMutation$data,
		useRequestSpaceAccessMutationRequestSpaceAccessMutation$variables
	>;
	isRequestAccessPending: boolean | undefined;
} => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isRequestAccessPending, setIsRequestAccessPending] = useState<boolean>();

	const getErrorMessage = (errorMessage: string, errorStatusCode: number) => {
		if (errorStatusCode === 409) {
			window.location.reload();
			return null;
		} else if (errorStatusCode === 404 && errorMessage.includes('Space Creator is unavailable')) {
			return {
				errorFlagTitle: <FormattedMessage {...i18nFlags.spaceCreatorUnavailableErrorFlagTitle} />,
				errorFlagMessage: (
					<FormattedMessage {...i18nFlags.spaceCreatorUnavailableErrorFlagDescription} />
				),
				errorFlagType: 'spaceCreatorUnavailable',
			};
		}
		return {
			errorFlagTitle: <FormattedMessage {...i18nFlags.generalErrorFlagTitle} />,
			errorFlagMessage: <FormattedMessage {...i18nFlags.generalErrorFlagDescription} />,
			errorFlagType: 'generalError',
		};
	};
	const [updateSpaceAccessMutation] = useMutationInterop<
		useRequestSpaceAccessMutationRequestSpaceAccessMutation$data,
		useRequestSpaceAccessMutationRequestSpaceAccessMutation$variables
	>(useRequestSpaceAccessMutationRequestSpaceAccessMutation, {
		onError: (data) => {
			const errorStatusCode = data?.graphQLErrors[0]?.extensions?.statusCode;
			const errorMessage = data?.graphQLErrors[0]?.message;
			const errorFlagCopies = getErrorMessage(errorMessage, errorStatusCode);
			if (errorFlagCopies) {
				void flags.showErrorFlag({
					title: errorFlagCopies.errorFlagTitle,
					description: errorFlagCopies.errorFlagMessage,
				});
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						source: 'restrictedSpaceScreen',
						actionSubject: 'flag',
						action: 'shown',
						actionSubjectId: 'errorFlag',
						attributes: {
							errorFlagTitle: errorFlagCopies.errorFlagType,
						},
					},
				}).fire();
			}
			setIsRequestAccessPending(false);
		},
		onCompleted: () => {
			void flags.showSuccessFlag({
				title: <FormattedMessage {...i18nFlags.successFlagTitle} />,
				// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
				description: !fg('access-request-dialog-content-fixes') ? (
					<FormattedMessage {...i18nFlags.successFlagDescription} />
				) : null,
			});
			setIsRequestAccessPending(true);
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'restrictedSpaceScreen',
					actionSubject: 'button',
					action: 'clicked',
					actionSubjectId: 'requestSpaceAccess',
				},
			}).fire();
		},
	});
	return { updateSpaceAccessMutation, isRequestAccessPending };
};
